'use client';

import { useRef, useState } from 'react';
import { Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { postMultipartImageUpload } from '@/lib/vertex-upload';

interface ProjectBannerFieldProps {
  bannerUrl: string;
  uploadFolder?: string;
  onChange: (url: string) => void;
  disabled?: boolean;
}

/** Optional banner: full-width display with max-height; no cropping (upload raw image). */
export function ProjectBannerField({
  bannerUrl,
  uploadFolder = 'projects/banners',
  onChange,
  disabled,
}: ProjectBannerFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function uploadFile(file: File) {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', uploadFolder);
      const { url } = await postMultipartImageUpload(formData);
      onChange(url);
      toast.success('Banner uploaded');
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Banner upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-base font-semibold">Banner image (optional)</Label>
        <p className="text-sm text-muted-foreground mt-1">
          Showcase your project with a banner image.
        </p>
      </div>
      {bannerUrl ? (
        <div className="relative space-y-2">
          <div className="w-full rounded-lg border border-border overflow-hidden bg-muted/40">
            <img src={bannerUrl} alt="" className="w-full max-h-[40vh] object-contain" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled || uploading}
              onClick={() => inputRef.current?.click()}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1" /> Uploading…
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-1" />
                  Replace
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive"
              disabled={disabled || uploading}
              onClick={() => onChange('')}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Remove banner
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Uploading…
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Upload banner
            </>
          )}
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        disabled={disabled || uploading}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) uploadFile(f);
        }}
      />
    </div>
  );
}
